import {graphqlQuery} from '../utils/graphqlQuery';
import {getFormattedReviewDate} from '../utils/dateUtils';

const OPERATION_NAME = 'graphql_get_reviews';

function enrichReviews(reviews, siteLanguage, formattedLocale) {
    if (Array.isArray(reviews)) {
        return reviews.map((item) => {
            return {
                ...item,
                ownershipTransferred: item.ownershipTransferred === 'true',
                isNonSiteLanguageReview: item.reviewLanguage !== siteLanguage,
                // review.arrivalDate & review.datePublished dates come as ISO string "2017-04-20T00:00:00Z"
                arrivalDate: getFormattedReviewDate(item.arrivalDate, formattedLocale, false),
                createdDate: getFormattedReviewDate(item.datePublished, formattedLocale, true)
            };
        });
    }

    return [];
}

function prepareResponse(response, {page, pageSize, siteLanguage, formattedLocale} = {}) {
    const {reviews, reviewSummary, ownerRating} = response;

    // if we received a review summary that has a non-zero number of guestbook reviews,
    // adjust the "reviewCount" field to be the number of reviews we can actually display to the user
    if (reviewSummary && reviewSummary.guestbookReviewCount > 0) {
        reviewSummary.reviewCount -= reviewSummary.guestbookReviewCount;
    }

    const enrichedReviews = enrichReviews(reviews, siteLanguage, formattedLocale);

    return {
        reviews: enrichedReviews,
        reviewSummary,
        ownerRating,
        paging: {
            page,
            pageSize
        }
    };
}

/* istanbul ignore next */
const reviewsQuery = `
    query ($isInitial: Boolean = false, $listingId: String!, $page: Int!, $pageSize: Int!, $filterBy: [String]){
        ownerRating(listingId: $listingId) {
            average,
            reviewCount
        }
        reviews(listingId: $listingId, page: $page, pageSize: $pageSize, filterBy: $filterBy) {
            uuid
            headline: title
            rating
            body: text
            arrivalDate
            datePublished
            ownershipTransferred
            voteCount
            reviewLanguage
            reviewer {
                location
                nickname
                profileUrl
                profilePhotoUrl
                profileImage
            }
            response: reviewResponse {
                status
                body
                language
                country
            }
        }
        reviewSummary (listingId: $listingId) @include(if: $isInitial) {
            reviewCount
            guestbookReviewCount
            averageRating
            oneStarReviewCount
            twoStarReviewCount
            threeStarReviewCount
            fourStarReviewCount
            fiveStarReviewCount
        }
    }
`;

async function reviewsGraphQl({
    listingId,
    siteLanguage = 'en',
    formattedLocale = 'en-US',
    page = 1,
    pageSize = 10,
    filterBy = []
}) {
    const response = await graphqlQuery(OPERATION_NAME, reviewsQuery, {
        isInitial: false,
        listingId,
        page,
        pageSize,
        filterBy
    });

    if (response.errors || !response.reviews) {
        return null;
    }

    return prepareResponse(response, {
        page,
        pageSize,
        siteLanguage,
        formattedLocale
    });
}

export default reviewsGraphQl;
export {reviewsGraphQl, enrichReviews, OPERATION_NAME, reviewsQuery, prepareResponse};
